import {differenceInDays} from 'date-fns';
import {isFeatureEnabled} from 'feature-flags';
import {Tooltip} from 'platform/components';
import {
  AdaptiveImage,
  Box,
  CSSDimension,
  HStack,
  Image,
  Integer,
  RatioImageProps,
  SizedImageProps,
} from 'platform/foundation';

import {CSSProperties} from 'react';

import {isNotEmpty, isNotNil} from 'ramda';

import {Flag, FlagProps} from '../../components/Flag/Flag';
import i18n from '../../i18n/i18n';
import {AvailableCar} from '../../types/AvailableCar';
import {PriceReportType} from '../../types/PriceReportType';
import {featureFlags} from '../../utils/featureFlags';
import {formatSuspiciousReasons} from '../../utils/formatSuspiciousReasons';
import {isSoldIn30Days} from '../../utils/isSoldIn30Days';
import {isSoldIn90Days} from '../../utils/isSoldIn90Days';
import {makeStorageUrl} from '../../utils/makeStorageUrl';

const DATA_TEST_IDS = {
  YOUR_VEHICLE_FLAG: 'yourVehicleFlag',
  YOUR_AD_FLAG: 'yourAdFlag',
  ADVERTISEMENTS_FLAG: 'advertisements',
  SUSPICIOUS_FLAG: 'suspiciousFlag',
} as const;

type VehicleImageFlagProps = Pick<
  FlagProps,
  'backgroundColor' | 'color' | 'label' | 'data-testid'
> & {
  tooltipLabel?: string | null;
};

interface VehicleImageProps {
  priceReport: PriceReportType;
  vehicle?: AvailableCar;
  isMyVehicle?: boolean;
  maxWidth?: CSSDimension | Integer;
  maxHeight?: CSSDimension | Integer;
  ratio?: CSSProperties['aspectRatio'];
}

export function VehicleImage(props: VehicleImageProps) {
  const isMyAd =
    isNotNil(props.priceReport.vehicle.adId) &&
    props.priceReport.vehicle.adId === props.vehicle?.ad_id;

  const soldDaysAgo = isNotNil(props.vehicle?.last_occurrence)
    ? differenceInDays(props.priceReport.created, props.vehicle.last_occurrence)
    : null;

  const soldLabel = isNotNil(props.vehicle?.last_occurrence)
    ? i18n.t('page.advertisements.soldNDaysAgo', {
        count: soldDaysAgo,
      })
    : i18n.t('page.advertisements.sold');

  const adaptiveImageSizeProps: RatioImageProps | SizedImageProps = isNotNil(props.ratio)
    ? {
        ratio: props.ratio,
        maxWidth: props.maxWidth,
        maxHeight: props.maxHeight,
      }
    : {
        width: props.maxWidth ?? '100%',
        height: props.maxHeight ?? '100%',
      };

  const getFlagProps = (): VehicleImageFlagProps | null => {
    if (props.isMyVehicle && isNotNil(props.priceReport.vehicle.imageUrl)) {
      return {
        backgroundColor: 'palettes.blue.70.100',
        color: 'palettes.white.10.100',
        label: i18n.t('page.advertisements.yourVehicle'),
        'data-testid': DATA_TEST_IDS.YOUR_VEHICLE_FLAG,
      };
    }
    if (isMyAd) {
      return {
        backgroundColor: 'palettes.green.60.100',
        color: 'palettes.white.10.100',
        label: i18n.t('page.advertisements.yourAd'),
        'data-testid': DATA_TEST_IDS.YOUR_AD_FLAG,
      };
    }
    if (isNotNil(props.vehicle) && isSoldIn30Days(props.priceReport, props.vehicle)) {
      return {
        backgroundColor: 'palettes.neutral.300.100',
        color: 'palettes.white.10.100',
        label: soldLabel,
        'data-testid': DATA_TEST_IDS.ADVERTISEMENTS_FLAG,
      };
    }
    if (isNotNil(props.vehicle) && isSoldIn90Days(props.priceReport, props.vehicle)) {
      return {
        backgroundColor: 'palettes.neutral.40.100',
        color: 'palettes.neutral.800.100',
        label: soldLabel,
        'data-testid': DATA_TEST_IDS.ADVERTISEMENTS_FLAG,
      };
    }
    if (isFeatureEnabled(featureFlags.PR_SUSPICIOUS_FLAG) && props.vehicle?.suspicious) {
      return {
        backgroundColor: 'severity.danger',
        color: 'palettes.white.10.100',
        label: i18n.t('page.advertisiments.suspicious'),
        tooltipLabel: formatSuspiciousReasons(props.vehicle.suspicious_reason),
        'data-testid': DATA_TEST_IDS.SUSPICIOUS_FLAG,
      };
    }
    return null;
  };
  const flagProps = getFlagProps();
  const imageUrl = props.isMyVehicle
    ? props.priceReport.vehicle.imageUrl
    : props.vehicle?.thumbnail_url;

  const renderFlag = () => {
    if (!flagProps) {
      return null;
    }

    const {tooltipLabel, ...flagComponentProps} = flagProps;
    const flag = <Flag size="small" {...flagComponentProps} />;

    return isNotNil(tooltipLabel) ? <Tooltip label={tooltipLabel}>{flag}</Tooltip> : flag;
  };

  return (
    <Box
      position="relative"
      backgroundColor={props.isMyVehicle ? 'palettes.green.20.100' : undefined}
      maxHeight={props.maxHeight}
      maxWidth={props.maxWidth}
      flexShrink={0}
    >
      <AdaptiveImage
        {...adaptiveImageSizeProps}
        fit="cover"
        url={(isNotEmpty(imageUrl) && imageUrl) || undefined}
        makeUrl={makeStorageUrl}
        data-testid="vehicleImage"
        placeholder={
          <Image
            width="100%"
            height="100%"
            fit="cover"
            ratio={props.ratio}
            src="/assets/images/car-placeholder.jpg"
            data-testid="vehicleImagePlaceholder"
          />
        }
      />
      <Box position="absolute" left={2} top={2}>
        <HStack spacing={1}>{renderFlag()}</HStack>
      </Box>
    </Box>
  );
}
